/**
 * Page Designer Grid V1 — grid-only.
 *
 * Screen = Canvas, Module = Card, Grid = Structure.
 * The grid is the SINGLE source of truth for card geometry:
 *   column, row, width, height — integer cells, nothing else.
 *
 * There is deliberately no second editable coordinate system. Cards move
 * and resize by whole cells; the grid is fine enough (60×40) that cell
 * steps give precise placement without per-card pixel offsets. Pixels
 * appear only as rendering/pointer implementation detail (see CELL_SIZE,
 * canvasCellSize, cellDeltaFromPixels) and are never stored on a card.
 *
 * Cells are always square. Width drives cell size; height is derived.
 * This is application screen layout — not printable report layout.
 * No EMR/FHIR data is attached to cards yet.
 * RTF Text is static formatted HTML stored on the card (rtfHtml).
 */

import { sanitizeRtfHtml } from "@/lib/pageDesignerRtf";
import {
  CONTENT_TYPE_SEARCH,
  SEARCH_PREFERRED_HEIGHT,
  SEARCH_PREFERRED_WIDTH,
  emptySearchContent,
  sanitizeSearchContent,
  searchContentEquals,
} from "@/lib/pageDesignerSearch";
import {
  CONTENT_TYPE_TABLE,
  TABLE_PREFERRED_HEIGHT,
  TABLE_PREFERRED_WIDTH,
  emptyTableContent,
  sanitizeTableContent,
  tableContentEquals,
} from "@/lib/pageDesignerTable";

export const GRID_COLUMNS = 60;
export const GRID_ROWS = 40;
/**
 * Base design unit in CSS pixels — rendering metadata only, never stored
 * on a card. The design page stays 1440×960 (60 × 24 by 40 × 24); the
 * actual cell size scales with container width.
 */
export const CELL_SIZE = 24;
export const MIN_CARD_SIZE = 1;

export function gridSizeKey(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return `${columns}x${rows}`;
}

export function gridSizeLabel(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return `${columns} × ${rows} Grid`;
}

export const GRID_SIZE_LABEL = gridSizeLabel();

export const CONTENT_TYPE_BLANK = "blank";
export const CONTENT_TYPE_RTF = "rtf";
export { CONTENT_TYPE_TABLE, CONTENT_TYPE_SEARCH };
export const BEHAVIOR_NONE = "none";

/** Content Type options. Blank, RTF Text, Table and Search are functional in this phase. */
export const CONTENT_TYPE_OPTIONS = Object.freeze([
  Object.freeze({
    id: CONTENT_TYPE_BLANK,
    label: "Blank",
    status: "ready",
  }),
  Object.freeze({
    id: CONTENT_TYPE_RTF,
    label: "RTF Text",
    status: "ready",
  }),
  Object.freeze({
    id: CONTENT_TYPE_TABLE,
    label: "Table",
    status: "ready",
  }),
  Object.freeze({
    id: CONTENT_TYPE_SEARCH,
    label: "Search",
    status: "ready",
  }),
  Object.freeze({
    id: "smartphrase",
    label: "SmartPhrase",
    status: "coming-soon",
    soonLabel: "SmartPhrase content is not available yet.",
  }),
  Object.freeze({
    id: "emr-data",
    label: "EMR Data",
    status: "coming-soon",
    soonLabel: "EMR data binding is not available yet.",
  }),
  Object.freeze({
    id: "image",
    label: "Image",
    status: "coming-soon",
    soonLabel: "image content is not available yet.",
  }),
]);

/** Behavior options. Only None is functional in this phase. */
export const BEHAVIOR_OPTIONS = Object.freeze([
  Object.freeze({
    id: BEHAVIOR_NONE,
    label: "None",
    status: "ready",
  }),
  Object.freeze({
    id: "button",
    label: "Button / Clickable",
    status: "coming-soon",
    soonLabel: "click actions are not wired yet. A small card can later behave as a button.",
  }),
  Object.freeze({
    id: "open-page",
    label: "Open Page",
    status: "coming-soon",
    soonLabel: "page navigation is not available yet.",
  }),
  Object.freeze({
    id: "open-section",
    label: "Open Section",
    status: "coming-soon",
    soonLabel: "section navigation is not available yet.",
  }),
  Object.freeze({
    id: "run-smartphrase",
    label: "Run SmartPhrase",
    status: "coming-soon",
    soonLabel: "running a SmartPhrase from a card is not available yet.",
  }),
]);

function optionById(options, id) {
  return options.find((option) => option.id === id) || null;
}

export function contentTypeOption(id) {
  return optionById(CONTENT_TYPE_OPTIONS, id) || CONTENT_TYPE_OPTIONS[0];
}

export function behaviorOption(id) {
  return optionById(BEHAVIOR_OPTIONS, id) || BEHAVIOR_OPTIONS[0];
}

/** Grid V1 opens blank. Cards are added later; do not seed demo cards. */
export const DEFAULT_CARDS = Object.freeze([]);

/** Subtle teal used only for cards created with + Add New Card. */
export const NEW_CARD_BACKGROUND = "#E8F4F2";
export const NEW_CARD_BORDER = "#5E9E90";
/**
 * Default size of a new card, in cells. 4×4 on the 60×40 grid is exactly
 * 1/15 of the page width and 1/10 of its height — a card you can read and
 * click, not a single speck of the (deliberately fine) grid.
 */
export const NEW_CARD_PREFERRED_WIDTH = 4;
export const NEW_CARD_PREFERRED_HEIGHT = 4;
export const NO_GRID_SPACE_MESSAGE =
  `No available space on the ${GRID_SIZE_LABEL}. Resize or move an existing card.`;
const CARD_ID_RE = /^card-\d+$/;
const CARD_TITLE_RE = /^CARD\s+(\d+)$/i;

function asInt(value, fallback) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

export function cloneCards(cards = DEFAULT_CARDS) {
  return (cards || []).map((card) => ({ ...card }));
}

export function defaultCards() {
  return cloneCards(DEFAULT_CARDS);
}

export function axisLabels(count) {
  const length = Math.max(0, asInt(count, 0));
  return Array.from({ length }, (_, index) => index + 1);
}

export function gridColumnLabels(columns = GRID_COLUMNS) {
  return axisLabels(columns);
}

export function gridRowLabels(rows = GRID_ROWS) {
  return axisLabels(rows);
}

export function gridAxisLabels() {
  return gridColumnLabels();
}

export function gridCellCount(columns = GRID_COLUMNS, rows = GRID_ROWS) {
  return columns * rows;
}

/**
 * Square-cell metrics for any column/row combination.
 * Cell size is derived from available width so cells never become rectangles.
 */
export function gridMetrics(
  availableWidth,
  { columns = GRID_COLUMNS, rows = GRID_ROWS, baseCellSize = CELL_SIZE } = {},
) {
  const width = Math.max(0, Number(availableWidth) || 0);
  const cols = Math.max(1, asInt(columns, GRID_COLUMNS));
  const rowCount = Math.max(1, asInt(rows, GRID_ROWS));
  const cellSize = width > 0 ? width / cols : baseCellSize;
  return Object.freeze({
    columns: cols,
    rows: rowCount,
    cellSize,
    gridWidth: cellSize * cols,
    gridHeight: cellSize * rowCount,
  });
}

export function gridCssVars({
  columns = GRID_COLUMNS,
  rows = GRID_ROWS,
  cellSize = CELL_SIZE,
} = {}) {
  return {
    "--epd-cols": String(columns),
    "--epd-rows": String(rows),
    "--epd-cell-base": `${cellSize}px`,
  };
}

/** Every card lives in the one page coordinate space, so col/row are always comparable. */
export function cardsOverlap(a, b) {
  if (!a || !b || a.id === b.id) return false;
  return (
    a.col < b.col + b.width &&
    a.col + a.width > b.col &&
    a.row < b.row + b.height &&
    a.row + a.height > b.row
  );
}

export function layoutHasOverlap(cards) {
  for (let i = 0; i < cards.length; i += 1) {
    for (let j = i + 1; j < cards.length; j += 1) {
      if (cardsOverlap(cards[i], cards[j])) return true;
    }
  }
  return false;
}

/**
 * The page-containment invariant. The whole 60×40 page is the one and only
 * boundary a card is checked against:
 *   col >= 1, row >= 1, col + width - 1 <= 60, row + height - 1 <= 40
 */
export function cardIsInsideGrid(card) {
  return (
    card.col >= 1 &&
    card.row >= 1 &&
    card.width >= MIN_CARD_SIZE &&
    card.height >= MIN_CARD_SIZE &&
    card.col + card.width - 1 <= GRID_COLUMNS &&
    card.row + card.height - 1 <= GRID_ROWS
  );
}

export function layoutIsInsideGrid(cards) {
  return (cards || []).every((card) => cardIsInsideGrid(card));
}

export function clampMove(card) {
  const width = Math.max(MIN_CARD_SIZE, asInt(card.width, MIN_CARD_SIZE));
  const height = Math.max(MIN_CARD_SIZE, asInt(card.height, MIN_CARD_SIZE));
  const maxCol = GRID_COLUMNS - width + 1;
  const maxRow = GRID_ROWS - height + 1;
  return {
    ...card,
    width,
    height,
    col: Math.min(Math.max(1, asInt(card.col, 1)), maxCol),
    row: Math.min(Math.max(1, asInt(card.row, 1)), maxRow),
  };
}

export function clampResize(card) {
  const col = Math.min(Math.max(1, asInt(card.col, 1)), GRID_COLUMNS);
  const row = Math.min(Math.max(1, asInt(card.row, 1)), GRID_ROWS);
  const maxWidth = GRID_COLUMNS - col + 1;
  const maxHeight = GRID_ROWS - row + 1;
  return {
    ...card,
    col,
    row,
    width: Math.min(Math.max(MIN_CARD_SIZE, asInt(card.width, MIN_CARD_SIZE)), maxWidth),
    height: Math.min(Math.max(MIN_CARD_SIZE, asInt(card.height, MIN_CARD_SIZE)), maxHeight),
  };
}

/** Repairs any card into a valid page-contained card. Never fails, never drops a card. */
export function clampCard(card) {
  return clampResize(clampMove(card));
}

export function cellDeltaFromPixels(dx, dy, cellWidth, cellHeight) {
  const width = Number(cellWidth) || 0;
  const height = Number(cellHeight) || 0;
  return {
    col: width ? Math.round(Number(dx) / width) : 0,
    row: height ? Math.round(Number(dy) / height) : 0,
  };
}

export function canvasCellSize(rect, columns = GRID_COLUMNS) {
  const width = Number(rect?.width) || 0;
  const cols = Math.max(1, asInt(columns, GRID_COLUMNS));
  const cellSize = width > 0 ? width / cols : 0;
  return {
    cellWidth: cellSize,
    cellHeight: cellSize,
  };
}

export function findCard(cards, id) {
  return (cards || []).find((card) => card.id === id) || null;
}

/**
 * Apply a patch if the result stays inside the page and does not overlap
 * another card. Returns a new cards array, or null when the change is
 * invalid.
 */
export function tryUpdateCard(cards, id, patch, mode = "move") {
  const current = findCard(cards, id);
  if (!current) return null;
  const merged = { ...current, ...patch };
  const nextCard = mode === "resize" ? clampResize(merged) : clampMove(merged);
  if (!cardIsInsideGrid(nextCard)) return null;
  const colliding = (cards || []).some((card) => card.id !== id && cardsOverlap(nextCard, card));
  if (colliding) return null;
  return (cards || []).map((card) => (card.id === id ? nextCard : card));
}

function walkCardChange(cards, id, targetPatch, mode) {
  const current = findCard(cards, id);
  if (!current) return null;
  const merged = { ...current, ...targetPatch };
  const target = mode === "resize" ? clampResize(merged) : clampMove(merged);
  const dCol = (mode === "resize" ? target.width : target.col) - (mode === "resize" ? current.width : current.col);
  const dRow = (mode === "resize" ? target.height : target.row) - (mode === "resize" ? current.height : current.row);
  const steps = Math.max(Math.abs(dCol), Math.abs(dRow));
  if (steps === 0) return cards;
  let best = null;
  for (let step = 1; step <= steps; step += 1) {
    const nextPatch =
      mode === "resize"
        ? {
            width: current.width + Math.round((dCol * step) / steps),
            height: current.height + Math.round((dRow * step) / steps),
          }
        : {
            col: current.col + Math.round((dCol * step) / steps),
            row: current.row + Math.round((dRow * step) / steps),
          };
    const next = tryUpdateCard(cards, id, nextPatch, mode);
    if (!next) break;
    best = next;
  }
  return best;
}

/** Moves a card to a whole cell, sliding as far toward the target as the page and its neighbours allow. */
export function tryMoveCard(cards, id, col, row) {
  return walkCardChange(cards, id, { col, row }, "move");
}

/** Resizes a card in whole cells — the resize counterpart of tryMoveCard. */
export function tryResizeCard(cards, id, width, height) {
  return walkCardChange(cards, id, { width, height }, "resize");
}

/**
 * Moves a group of selected cards to explicit absolute cell targets at
 * once, atomically: either every card in `moves` lands on its target or,
 * if any target would leave the page or the resulting layout would
 * overlap, none of them move. `moves` is an array of `{ id, col, row }`.
 * This is the multi-card counterpart of tryMoveCard, used for dragging or
 * nudging 2+ selected cards together while keeping their relative
 * positions; a lone card in `moves` still goes through the same
 * bounds/overlap check tryMoveCard itself uses, just without its per-step
 * "slide to the last open cell" walk (each card's target here is a fixed
 * absolute delta from the drag anchor, not a value to walk toward).
 */
export function tryMoveCardsTo(cards, moves) {
  const list = Array.isArray(moves) ? moves : [];
  if (list.length === 0) return null;
  const patches = new Map();
  for (const { id, col, row } of list) {
    const current = findCard(cards, id);
    if (!current) return null;
    const nextCard = { ...current, col: asInt(col, current.col), row: asInt(row, current.row) };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(id, nextCard);
  }
  const candidate = (cards || []).map((card) => patches.get(card.id) || card);
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/**
 * Moves every selected card by the same whole-cell delta — keyboard
 * arrow-key movement. The selection travels rigidly and atomically: if
 * any card would leave the page or collide, nothing moves, so the group's
 * relative arrangement can never be deformed by nudging into an edge.
 */
export function nudgeCardsCells(cards, ids, dCol, dRow) {
  const selected = selectedCards(cards, ids, 1);
  if (!selected) return null;
  const col = asInt(dCol, 0);
  const row = asInt(dRow, 0);
  return tryMoveCardsTo(
    cards,
    selected.map((card) => ({ id: card.id, col: card.col + col, row: card.row + row })),
  );
}

const ALIGN_LEFT = "left";
const ALIGN_RIGHT = "right";
const ALIGN_TOP = "top";
const ALIGN_BOTTOM = "bottom";
const ALIGN_CENTER_HORIZONTAL = "center-horizontal";
const ALIGN_CENTER_VERTICAL = "center-vertical";

/** Resolves ids to cards for a multi-card op, or null when the group doesn't meet `minCount`. */
function selectedCards(cards, ids, minCount = 1) {
  const uniqueIds = Array.from(new Set(ids || []));
  if (uniqueIds.length < minCount) return null;
  const selected = uniqueIds.map((id) => findCard(cards, id));
  if (selected.some((card) => !card)) return null;
  return selected;
}

/** Selected cards for an alignment op, or null when the group is not alignable. */
function alignableSelection(cards, ids) {
  return selectedCards(cards, ids, 2);
}

/**
 * Grid-cell alignment. Target edges/centers are computed once from the
 * selection's own bounding box in cell units, then every selected card is
 * moved to a whole cell — the same contract as tryMoveCard. Validated
 * against the whole resulting layout (page bounds + overlap) and rejected
 * atomically: either every selected card moves, or none do. Only col/row
 * change, so the result is always valid grid geometry.
 */
function alignCards(cards, ids, edge) {
  const selected = alignableSelection(cards, ids);
  if (!selected) return null;

  const left = Math.min(...selected.map((card) => card.col));
  const right = Math.max(...selected.map((card) => card.col + card.width));
  const top = Math.min(...selected.map((card) => card.row));
  const bottom = Math.max(...selected.map((card) => card.row + card.height));

  const patches = new Map();
  for (const card of selected) {
    let col = card.col;
    let row = card.row;
    switch (edge) {
      case ALIGN_LEFT:
        col = left;
        break;
      case ALIGN_RIGHT:
        col = right - card.width;
        break;
      case ALIGN_TOP:
        row = top;
        break;
      case ALIGN_BOTTOM:
        row = bottom - card.height;
        break;
      case ALIGN_CENTER_HORIZONTAL:
        col = Math.round((left + right) / 2 - card.width / 2);
        break;
      case ALIGN_CENTER_VERTICAL:
        row = Math.round((top + bottom) / 2 - card.height / 2);
        break;
      default:
        return null;
    }
    const nextCard = { ...card, col, row };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
  }

  const candidate = (cards || []).map((card) => patches.get(card.id) || card);
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

export function alignCardsLeft(cards, ids) {
  return alignCards(cards, ids, ALIGN_LEFT);
}

export function alignCardsRight(cards, ids) {
  return alignCards(cards, ids, ALIGN_RIGHT);
}

export function alignCardsTop(cards, ids) {
  return alignCards(cards, ids, ALIGN_TOP);
}

export function alignCardsBottom(cards, ids) {
  return alignCards(cards, ids, ALIGN_BOTTOM);
}

/** Aligns selected cards so their horizontal (X) centers match. */
export function alignCardsCenterHorizontal(cards, ids) {
  return alignCards(cards, ids, ALIGN_CENTER_HORIZONTAL);
}

/** Aligns selected cards so their vertical (Y) centers match. */
export function alignCardsCenterVertical(cards, ids) {
  return alignCards(cards, ids, ALIGN_CENTER_VERTICAL);
}

const DISTRIBUTE_HORIZONTAL = "horizontal";
const DISTRIBUTE_VERTICAL = "vertical";

/**
 * Grid-cell distribute. Requires 3+ selected cards — with only 2 there is
 * nothing "between" them to space out. Sorts the selection along the axis,
 * holds the two extreme cards (by leading edge) fixed, and spaces every
 * card in between so the gaps between adjacent edges are equal, matching
 * the leftover space (bounding span minus the sum of every card's own
 * size) evenly across the gaps. Validated against the whole resulting
 * layout (page bounds + overlap) and rejected atomically, like alignCards.
 * Only col/row change, so the result is always valid grid geometry.
 */
function distributeCards(cards, ids, axis) {
  const selected = selectedCards(cards, ids, 3);
  if (!selected) return null;
  const posKey = axis === DISTRIBUTE_HORIZONTAL ? "col" : "row";
  const sizeKey = axis === DISTRIBUTE_HORIZONTAL ? "width" : "height";

  const sorted = [...selected].sort((a, b) => a[posKey] - b[posKey]);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = last[posKey] + last[sizeKey] - first[posKey];
  const sumSizes = sorted.reduce((total, card) => total + card[sizeKey], 0);
  const gap = (span - sumSizes) / (sorted.length - 1);

  const patches = new Map();
  let cursor = first[posKey] + first[sizeKey];
  for (let i = 1; i < sorted.length - 1; i += 1) {
    const card = sorted[i];
    cursor += gap;
    const pos = Math.round(cursor);
    const nextCard = { ...card, [posKey]: pos };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
    cursor = pos + card[sizeKey];
  }

  const candidate = (cards || []).map((card) => patches.get(card.id) || card);
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/** Distributes selected cards so the horizontal gaps between them are equal. */
export function distributeCardsHorizontally(cards, ids) {
  return distributeCards(cards, ids, DISTRIBUTE_HORIZONTAL);
}

/** Distributes selected cards so the vertical gaps between them are equal. */
export function distributeCardsVertically(cards, ids) {
  return distributeCards(cards, ids, DISTRIBUTE_VERTICAL);
}

/** Selected cards for a Same Width/Height/Size op — the same 2+ threshold as alignment. */
function sameSizeSelection(cards, ids) {
  return selectedCards(cards, ids, 2);
}

/**
 * Resizes every selected card to the given width/height (grid cells),
 * anchored at its own existing col/row — the same top-left anchor
 * tryResizeCard uses — atomically: either every card fits on the page
 * without overlapping another card, or none of them resize.
 */
function matchCardsDimensions(cards, ids, dims) {
  const selected = sameSizeSelection(cards, ids);
  if (!selected) return null;
  const patches = new Map();
  for (const card of selected) {
    const width = dims.width ?? card.width;
    const height = dims.height ?? card.height;
    const nextCard = { ...card, width, height };
    if (!cardIsInsideGrid(nextCard)) return null;
    patches.set(card.id, nextCard);
  }
  const candidate = (cards || []).map((card) => patches.get(card.id) || card);
  if (layoutHasOverlap(candidate)) return null;
  return candidate;
}

/** Sets every selected card's width to match the reference card's width. */
export function matchCardsWidth(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { width: reference.width });
}

/** Sets every selected card's height to match the reference card's height. */
export function matchCardsHeight(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { height: reference.height });
}

/** Sets every selected card's width and height to match the reference card's size. */
export function matchCardsSize(cards, ids, referenceId) {
  const reference = findCard(cards, referenceId);
  if (!reference) return null;
  return matchCardsDimensions(cards, ids, { width: reference.width, height: reference.height });
}

export function tryStyleCard(cards, id, patch) {
  const current = findCard(cards, id);
  if (!current) return null;
  return (cards || []).map((card) => (card.id === id ? { ...card, ...patch } : card));
}

export function removeCard(cards, id) {
  if (!id || !findCard(cards, id)) return null;
  return (cards || []).filter((card) => card.id !== id);
}

/**
 * Duplicates a card into the first open cells on the page, shrinking the
 * copy only as far as the remaining rectangle requires — the same
 * placement contract Add Card uses. The copy is always valid grid
 * geometry inside the page; null when there is no room at all.
 */
export function duplicateCard(cards, id) {
  const source = findCard(cards, id);
  if (!source) return null;
  const placement = findFirstAvailablePlacement(cards, source.width, source.height);
  if (!placement) return null;
  const number = nextCardNumber(cards);
  return [
    ...(cards || []),
    {
      ...source,
      id: `card-${number}`,
      title: `CARD ${number}`,
      col: placement.col,
      row: placement.row,
      width: placement.width,
      height: placement.height,
    },
  ];
}

function defaultById(id) {
  return DEFAULT_CARDS.find((card) => card.id === id) || null;
}

function occupancyGrid(cards) {
  const occupied = Array.from({ length: GRID_ROWS }, () => Array(GRID_COLUMNS).fill(false));
  (cards || []).forEach((card) => {
    if (!cardIsInsideGrid(card)) return;
    for (let row = card.row; row < card.row + card.height; row += 1) {
      for (let col = card.col; col < card.col + card.width; col += 1) {
        occupied[row - 1][col - 1] = true;
      }
    }
  });
  return occupied;
}

export function nextCardNumber(cards) {
  let max = 0;
  (cards || []).forEach((card) => {
    const titleMatch = typeof card?.title === "string" ? card.title.trim().match(CARD_TITLE_RE) : null;
    const idMatch = typeof card?.id === "string" ? card.id.trim().match(/^card-(\d+)$/i) : null;
    if (titleMatch) max = Math.max(max, Number.parseInt(titleMatch[1], 10));
    if (idMatch) max = Math.max(max, Number.parseInt(idMatch[1], 10));
  });
  return max + 1;
}

/**
 * First empty cell on the page in row-major order, sized to the preferred
 * default or whatever remaining rectangle fits from that origin.
 */
export function findFirstAvailablePlacement(
  cards,
  preferredWidth = NEW_CARD_PREFERRED_WIDTH,
  preferredHeight = NEW_CARD_PREFERRED_HEIGHT,
) {
  const columns = GRID_COLUMNS;
  const rows = GRID_ROWS;
  const occupied = occupancyGrid(cards);
  const wantWidth = Math.max(MIN_CARD_SIZE, asInt(preferredWidth, NEW_CARD_PREFERRED_WIDTH));
  const wantHeight = Math.max(MIN_CARD_SIZE, asInt(preferredHeight, NEW_CARD_PREFERRED_HEIGHT));
  for (let row = 1; row <= rows; row += 1) {
    for (let col = 1; col <= columns; col += 1) {
      if (occupied[row - 1][col - 1]) continue;
      let maxWidth = 0;
      for (let nextCol = col; nextCol <= columns; nextCol += 1) {
        if (occupied[row - 1][nextCol - 1]) break;
        maxWidth += 1;
      }
      if (maxWidth < MIN_CARD_SIZE) continue;
      // Measure the free run downwards under the width this card will
      // actually take, not under the whole open run of the first row: a
      // card far off to the right must not shrink this one's height.
      const width = Math.min(wantWidth, maxWidth);
      let maxHeight = 0;
      heightLoop: for (let nextRow = row; nextRow <= rows; nextRow += 1) {
        for (let nextCol = col; nextCol < col + width; nextCol += 1) {
          if (occupied[nextRow - 1][nextCol - 1]) break heightLoop;
        }
        maxHeight += 1;
      }
      if (maxHeight < MIN_CARD_SIZE) continue;
      return {
        col,
        row,
        width,
        height: Math.min(wantHeight, maxHeight),
      };
    }
  }
  return null;
}

/** Places a new blank card in the first open cells on the page, at the default 4×4 size. */
export function createPlaceholderCard(cards) {
  const placement = findFirstAvailablePlacement(
    cards,
    NEW_CARD_PREFERRED_WIDTH,
    NEW_CARD_PREFERRED_HEIGHT,
  );
  if (!placement) return null;
  const number = nextCardNumber(cards);
  return {
    id: `card-${number}`,
    title: `CARD ${number}`,
    ...placement,
    background: NEW_CARD_BACKGROUND,
    border: NEW_CARD_BORDER,
    contentType: CONTENT_TYPE_BLANK,
    behavior: BEHAVIOR_NONE,
    rtfHtml: "",
    tableContent: emptyTableContent(),
    searchContent: emptySearchContent(),
  };
}

export function addPlaceholderCard(cards) {
  const card = createPlaceholderCard(cards);
  if (!card) return null;
  return [...(cards || []), card];
}

function sanitizeColor(value, fallback) {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) return trimmed;
  return fallback;
}

function sanitizeTitle(value, fallback) {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed || fallback;
}

function sanitizeChoice(value, options, fallback) {
  const id = typeof value === "string" ? value.trim() : "";
  return options.some((option) => option.id === id) ? id : fallback;
}

/**
 * Repairs a card whose cells collide with cards already accepted, by
 * relocating it — at its own full size — to the first open rectangle on
 * the page. Relocating must never resize: a card that came back smaller
 * than the user made it has been corrupted just as surely as one that
 * came back missing. When the page has no room for it at full size the
 * card is kept exactly where it is rather than thrown away or shrunk —
 * an overlapping card is recoverable, a mangled one is not.
 */
function placeWithoutOverlap(placed, card) {
  if (!placed.some((other) => cardsOverlap(card, other))) return card;
  const placement = findFirstAvailablePlacement(placed, card.width, card.height);
  if (!placement || placement.width !== card.width || placement.height !== card.height) return card;
  return { ...card, ...placement };
}

/**
 * Validates and REPAIRS a saved cards array. Normalization never throws
 * away the whole layout over geometry: each card is sanitized field by
 * field, clamped into the page with clampCard, and relocated if it would
 * sit on top of a card already accepted. One bad card can therefore never
 * take unrelated cards down with it, and Save can never write back fewer
 * cards than the user has.
 *
 * The only entries dropped are ones with no usable identity — a missing,
 * malformed, or duplicate card id — because there is nothing to repair
 * them into. Every other entry survives in some valid form.
 */
export function normalizeLayout(rawCards) {
  if (!Array.isArray(rawCards) || rawCards.length === 0) {
    return defaultCards();
  }
  const seen = new Set();
  const next = [];
  for (const saved of rawCards) {
    const id = typeof saved?.id === "string" ? saved.id.trim() : "";
    if (!CARD_ID_RE.test(id) || seen.has(id)) continue;
    seen.add(id);
    const defaults = defaultById(id);
    const number = Number.parseInt(id.slice(5), 10);
    const card = clampCard({
      id,
      title: sanitizeTitle(saved?.title, defaults?.title || `CARD ${number}`),
      col: asInt(saved?.col, defaults?.col ?? 1),
      row: asInt(saved?.row, defaults?.row ?? 1),
      width: asInt(saved?.width, defaults?.width ?? NEW_CARD_PREFERRED_WIDTH),
      height: asInt(saved?.height, defaults?.height ?? NEW_CARD_PREFERRED_HEIGHT),
      background: sanitizeColor(saved?.background, defaults?.background || NEW_CARD_BACKGROUND),
      border: sanitizeColor(saved?.border, defaults?.border || NEW_CARD_BORDER),
      contentType: sanitizeChoice(
        saved?.contentType,
        CONTENT_TYPE_OPTIONS,
        defaults?.contentType || CONTENT_TYPE_BLANK,
      ),
      behavior: sanitizeChoice(
        saved?.behavior,
        BEHAVIOR_OPTIONS,
        defaults?.behavior || BEHAVIOR_NONE,
      ),
      rtfHtml: sanitizeRtfHtml(saved?.rtfHtml || defaults?.rtfHtml || ""),
      tableContent: sanitizeTableContent(saved?.tableContent || defaults?.tableContent),
      searchContent: sanitizeSearchContent(saved?.searchContent || defaults?.searchContent),
    });
    next.push(placeWithoutOverlap(next, card));
  }
  return next;
}

export function layoutEquals(left, right) {
  if (!left || !right || left.length !== right.length) return false;
  return left.every((card, index) => {
    const other = right[index];
    return (
      card.id === other.id &&
      card.title === other.title &&
      card.col === other.col &&
      card.row === other.row &&
      card.width === other.width &&
      card.height === other.height &&
      card.background === other.background &&
      card.border === other.border &&
      (card.contentType || CONTENT_TYPE_BLANK) === (other.contentType || CONTENT_TYPE_BLANK) &&
      (card.behavior || BEHAVIOR_NONE) === (other.behavior || BEHAVIOR_NONE) &&
      (card.rtfHtml || "") === (other.rtfHtml || "") &&
      tableContentEquals(card.tableContent, other.tableContent) &&
      searchContentEquals(card.searchContent, other.searchContent)
    );
  });
}

/**
 * Attaches a Content Function to a card. A card still at the default 4×4
 * placeholder size is grown to the content's preferred size, because a 4×4
 * card cannot show a table or a search box; a card the user has already sized
 * is left exactly as they sized it.
 */
function applyContent(cards, id, patch, preferredWidth, preferredHeight) {
  const current = findCard(cards, id);
  if (!current) return null;
  let next = tryStyleCard(cards, id, patch);
  if (!next) return null;
  const card = findCard(next, id);
  const stillPlaceholder =
    card &&
    card.width <= NEW_CARD_PREFERRED_WIDTH &&
    card.height <= NEW_CARD_PREFERRED_HEIGHT;
  if (stillPlaceholder) {
    const grown = tryResizeCard(next, id, preferredWidth, preferredHeight);
    if (grown) next = grown;
  }
  return next;
}

export function applyTableContent(cards, id, tableContent) {
  return applyContent(
    cards,
    id,
    {
      contentType: CONTENT_TYPE_TABLE,
      tableContent: sanitizeTableContent(tableContent),
    },
    TABLE_PREFERRED_WIDTH,
    TABLE_PREFERRED_HEIGHT,
  );
}

export function applySearchContent(cards, id, searchContent) {
  return applyContent(
    cards,
    id,
    {
      contentType: CONTENT_TYPE_SEARCH,
      searchContent: sanitizeSearchContent(searchContent),
    },
    SEARCH_PREFERRED_WIDTH,
    SEARCH_PREFERRED_HEIGHT,
  );
}
